<template>
    <div class="app-wrapper">
        <div :class="`app-${app.uuid}`"> </div>

        <b-alert :show="errorModal.show" variant="danger">
            {{ errorModal.msg }}
        </b-alert>
    </div>
</template>

<script>

import { 
    Thunder,
    Component,
    Project,
    Integrations,
    Ratchet,
    Sandbox,
    Functions,
    PRR,
} from '@protokol/platform-libs'

import * as SDK from '@ptkl/sdk'
import * as SDK10 from 'ptklsdk10'
import printJS from 'print-js'
import axios from 'axios'
import moment from 'moment'
import _ from 'lodash'
import Vue from "vue"
import VueRouter from 'vue-router'
import * as CoreUI from '@/components/ui/vue/src'
import Compiler from '@/components/renderer'
import Loader from '@/components/renderer/loaders/engine'
import { Howl } from 'howler'
import PDFObject from 'pdfobject'

// adapters
import PlatformAdapter from '@/lib/adapters/platform'

// isolated app
import TemplateComponents from './templates'
import FactorI18N from './translation'

export default {
    name: 'PowerAppV4',
    components: {},
    props: {
        app: {
            type: Object
        },
        entrypoint: {
            type: String,
            default: "index"
        },
        dist: {
            type: Object
        },
        noRouter: {
            type: Boolean,
            default: false
        },
        version :{
           type:Number,
           default:1
        }
    },

    data: function () {
        return {
            context: {
                vars: {}
            },
            errorModal: {
                show: false,
                msg: null
            },
            modules: {},
            exports: {},
            components: {},
            services: {},
            packageImports: {},
            vueInstance: null
        }
    },

    methods: {
        showErrorModal(msg) {
            this.errorModal.show = true;
            this.errorModal.msg = msg;
        },

        find(collection, f) {
            return this.$store.getters.coldStorageFind(collection, f)
        },

        import(path) {
            return this.require(path)
        },

        require(path, options) {
            // get extension
            let parts = path.split(".")
            let ext = parts[parts.length - 1]
            let node = this.dist[path] || null
            if (!node) {
                if (!options.ignorePathError) {
                    this.showErrorModal("Can't resolve path (" + path + ") to import module.")
                }

                return;
            }

            if (ext == 'js') {
                try {
                    let m = (new Compiler()).evaluateJS(node, this.libs()).default
                    if (m && m.stack && m.message) {
                        throw new Error(m.message)
                    }

                    return m
                } catch (err) {
                    console.error(err)
                    throw new Error(err.message)
                }

            }

            if (ext == 'css') {
                this.parseStyles(node)
            }

            if (ext == 'svg') {
                return node
            }

            if (ext == 'json') {
                return JSON.parse(node)
            }
        },

        async register(node) {
            try {
                let compiler = new Compiler();
                let result = compiler.evaluateJS(node.script, this.libs()).default || {}
                if (result && result.stack && result.message) {
                    throw new Error(result.message)
                }

                result.template = node.template

                result.components = {
                    ...result.components,
                    ...CoreUI,
                    ...this.components,
                    ...TemplateComponents,
                }

                // scope styles only to the component
                this.parseStyles(node.style)

                return result
            } catch (err) {
                console.error(err)
                this.showErrorModal(err.message)
            }
        },

        async parseStyles(content) {
            // Create a new <style> element
            const style = document.createElement('style');

            // Define your CSS as a string
            const css = content;
            style.class = `appstyles-${this.app.uuid}`;
            // Set the content of the <style> element
            style.textContent = css;

            // Append the <style> element to the <head> or <body>
            document.head.appendChild(style);
        },

        displayError(message) {
            this.$bvToast.toast(message, {
                variant: 'danger',
                title: "Error"
            })
        },

        goToEditor() {
            this.$emit('goToEditor')
        },

        loader() {
            let loader = new Loader(this.dist, {
                jsLibs: this.libs()
            })

            loader.registerLoader('vue$', (node) => {
                return () => ({
                    component: this.register(node),
                })
            })

            loader.registerLoader('css$', (node) => {
                this.parseStyles(node)
            })

            return loader
        },

        libs() {
            return {
                $sdk: {
                    version(version) {
                        switch (version) {
                            case '0.10':
                                return SDK10;
                            default:
                                return SDK;
                        }
                    }
                },

                session: this.$store.getters.getUser,
                thunder: new Thunder(new PlatformAdapter(this)),
                component: new Component(new PlatformAdapter(this)),
                integrations: new Integrations(new PlatformAdapter(this)),
                ratchet: new Ratchet(new PlatformAdapter(this)),
                sandbox: new Sandbox(new PlatformAdapter(this)),
                project: new Project(new PlatformAdapter(this)),
                functions: new Functions(new PlatformAdapter(this)),
                prr: new PRR(new PlatformAdapter(this)),
                require: (path) => this.loader().require(path),
                print: printJS,
                axios: axios,
                moment: moment,
                lodash: _,
                localStorage: undefined,
                sessionStorage: undefined,

                log: {
                    info: (...args) => {
                        console.log(...args)
                    },

                    error: (err) => {
                        console.error(err)
                    }
                },

                util: {
                    dateToUserTimezone: this.$toUserTimeZone,
                    dateToTimezone: (time, zone, format) => {
                        let defaultFormat = 'YYYY-MM-DD HH:mm:ss';

                        if (format) {
                            defaultFormat = format
                        }

                        if (zone === null || zone === undefined || zone == "") {
                            return moment(time).tz(moment.tz.guess()).format(defaultFormat);
                        }

                        return moment(time).tz(zone).format(defaultFormat);
                    },

                    dateToUserTimezoneAsTime: (time) => {
                        return moment(this.$toUserTimeZone(time))
                    },

                    dateToTimezoneAsTime: (time, zone) => {
                        if (zone === null || zone === undefined || zone == "") {
                            return moment(time).tz(moment.tz.guess());
                        }

                        return moment(time).tz(zone);
                    },

                    compileTemplate: async (template) => {
                        let compiler = new Compiler();
                        let r = await compiler.compileVue(null, template)
                        let t = await this.register(r)

                        return Vue.extend(t)
                    },

                    registerComponent: (c) => {
                        c.components = {
                            ...c.components || {},
                            ...CoreUI,
                            ...this.components,
                            ...TemplateComponents,
                        }

                        return Vue.extend(c)
                    },

                    getPlatformLocale: () => {
                        return this.$i18n.locale
                    }
                },

                config: {
                    getEnv: (key, def) => {
                        let config = this.require('config.js', { ignorePathErrors: true })
                        let currentEnv = this.$store.getters.getCurrentEnv

                        config.env.live = Object.assign(config.env.live, _.get(this.app, 'config.live', {}))
                        config.env.dev = Object.assign(config.env.dev,  _.get(this.app, 'config.dev', {}))

                        return _.get(config.env, `${currentEnv}.${key}`, def)
                    },
                },

                app: {
                    showLoadingPopup: (t) => this.$loadingPopup(t),
                    openWindow: (link, target) => {
                        window.open(link, target)
                    },
                    
                    currentEnv: () => {
                        return this.$store.getters.getCurrentEnv
                    },

                    notify: (type, text) => {
                        this.$bvToast.toast(text, {
                            variant: type,
                            title: (type === 'success' ? 'Success' : (type === 'error' ? 'Error' : type)),
                        })
                    },

                    interval: {
                        set: (fn, tm) => {
                            return window.setInterval(function () {
                                fn()
                            }, tm)
                        },

                        clear: (interval) => {
                            window.clearInterval(interval)
                        }
                    },

                    audio: {
                        play: (file, options) => {
                            var sound = new Howl({
                                src: [file],
                                volume: 1,
                                ...options
                            });

                            sound.play();

                            return sound
                        },

                        playNotification: (options) => {
                            var sound = new Howl({
                                src: ['../sound/notification.wav'],
                                volume: 1,
                                ...options
                            });

                            sound.play();

                            return sound
                        }
                    },

                    eval(expr, params) {
                        let keys = Object.keys(params)
                        let values = Object.values(params)

                        let result = new Function(...keys, `try{ ${expr} } catch (err) { return err; }`)

                        return result(...values)
                    },

                    storage: {
                        get: (key) => {
                            return localStorage.getItem(`${this.app.uuid}_${key}`)
                        },

                        set: (key, value) => {
                            return localStorage.setItem(`${this.app.uuid}_${key}`, value)
                        },

                        remove: (key) => {
                            return localStorage.removeItem(`${this.app.uuid}_${key}`)
                        }
                    }
                },

                user: {
                    roles: async () => {
                        return this.$store.dispatch('fetchRoles')
                    },

                    can: (action) => {
                        return this.$can(action)
                    },

                    allowed: (action) => {
                        return this.$can(`${action} platformapp::${this.app.name}`)
                    },
                },

                event: {
                    listen(e, fn) {
                        window.addEventListener(e, (ev) => {
                            fn(ev)
                        })
                    }
                },

                loader: {
                    loadSVG: (path, name) => {
                        let content = this.require(path)
                        this.components[name] = Vue.extend(content)
                    }
                },

                file: {
                    readAsDataUrl: (file, options) => {
                        return new Promise((resolve, reject) => {
                            var reader = new FileReader()
                            reader.readAsDataURL(file, options)
                            reader.onload = () => resolve(reader.result);
                            reader.onerror = error => reject(error);
                        })
                    },

                    pdf: PDFObject
                },

                eventbus: this.$eventBus,

                voyager: {
                    async call(fname, ...args) {
                        if (!window.flutter_inappwebview) {
                            throw new Error("invalid platform");
                        }

                        return await window.flutter_inappwebview.callHandler(fname, ...args);
                    }
                },

                exports: {}
            }
        },

        compileApp() {
            let compiler = new Compiler();

            const { template, script, style } = this.template || {}
            let result = compiler.evaluateJS(script, this.libs()).default || {}

            if (result && result.stack && result.message) {
                this.showErrorModal(result.message)
                return
            }

            try {
                this.parseStyles(style)

                result.components = {
                    ...result.components,
                    ...CoreUI,
                    ...this.components,
                    ...TemplateComponents,
                }

                let component = Vue.extend({
                    name: `protokol-power-app-template-${this.app.uuid}`,
                    ...result,
                    template: template,
                })

                let router = null
                if (!this.noRouter) {
                    router = new VueRouter({
                        // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
                        routes: this.require('routes.js', { ignorePathError: true }) || [], // short for `routes: routes`
                        base: this.$route.path
                    })
                }

                let i18nopts = this.require('i18n.js', { ignorePathError: true }) || {}
                let i18n = FactorI18N(i18nopts)

                let storeopts = this.require('store.js', { ignorePathError: true }) || {}   
                Object.keys(storeopts).forEach(store => {     
                    this.$store.registerModule("", storeopts[store], { preserveState: false }) 
                })

                var self = this;
                this.vueInstance = new Vue({
                    el: `.app-${this.app.uuid}`,
                    template: '<PowerAppV4 v-on="inheritListeners" v-bind="inheritAttrs" />',
                    router,
                    store: this.$store,
                    i18n,
                    components: {
                        PowerAppV4: component
                    },

                    computed: {
                        inheritListeners() {
                            return self.$listeners
                        },

                        inheritAttrs() {
                            return self.$attrs
                        }
                    }
                })
            } catch (err) {
                console.error(err)
            }
        }
    },

    computed: {
        template() {
            try {
                return this.dist[this.entrypoint + ".vue"]
            } catch (err) {
                this.$debugLog(err)
                this.showErrorModal(`Did you properly formated your ${this.entrypoint} template?`);
            }
        },

        scopeId() {
            return this.app.uuid.replace(/[0-9]/g, '').replaceAll('-', '');
        }

    },

    destroyed() {
        this.$eventBus.close();
        document.querySelectorAll(`style#${CSS.escape(this.version)}`).forEach(style => style.remove())
    },

    mounted() {
        this.$eventBus.connect()

        this.compileApp()
    },

    beforeDestroy() {
        // Get a reference to the last interval + 1
        const interval_id = window.setInterval(function () { }, Number.MAX_SAFE_INTEGER);

        // Clear any timeout/interval up to that id
        for (let i = 1; i < interval_id; i++) {
            window.clearInterval(i);
        }

        document.querySelectorAll(`style.appstyles-${this.app.uuid}`).forEach(styleEl => {
            styleEl.remove();
        });
    },

    updated() {
        console.log("[Render] PowerAppV4 rendered")
    },
}
</script>